import { create } from 'zustand'
import api from '../lib/api'

/** SHA-256 con Web Crypto API nativa del navegador */
async function sha256hex(text: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text))
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('')
}

export type Role = 'ADMIN' | 'COLABORADOR' | 'CLIENTE' | 'MAYORISTA' | 'PRODUCCION' | 'SUPERVISOR' | 'ACCIONISTA'
export type Sede = 'TORO' | 'PEREIRA'

export interface User {
  id: number
  nombre: string
  email: string
  role: Role
  sede?: Sede
  avatar: string
  permisos?: string[]
}

interface AuthState {
  user: User | null
  token: string | null
  loading: boolean
  ready: boolean
  login: (email: string, password: string) => Promise<void>
  register: (data: { nombre: string; email: string; password: string; telefono?: string }) => Promise<void>
  setUser: (user: User, token: string) => void
  logout: () => void
  loadFromStorage: () => void
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  token: null,
  loading: false,
  ready: false,

  loadFromStorage: () => {
    try {
      const token = localStorage.getItem('aqua_token')
      const user = localStorage.getItem('aqua_user')
      if (token && user) {
        set({ token, user: JSON.parse(user), ready: true })
      } else {
        set({ ready: true })
      }
    } catch {
      localStorage.removeItem('aqua_token')
      localStorage.removeItem('aqua_user')
      set({ ready: true })
    }
  },

  login: async (email, password) => {
    set({ loading: true })
    try {
      // 1. Consultar qué formato espera el backend para este email
      const { data: fmt } = await api.get(`/auth/password-format?email=${encodeURIComponent(email)}`)
      // 2. Si es v2 (sha256) hashear; si es v1 (plain) enviar tal cual para migrar automáticamente
      const passwordToSend = fmt.format === 'sha256' ? await sha256hex(password) : password
      const { data } = await api.post('/auth/login', { email, password: passwordToSend })
      localStorage.setItem('aqua_token', data.access_token)
      localStorage.setItem('aqua_user', JSON.stringify(data.user))
      set({ token: data.access_token, user: data.user, loading: false })
    } catch (err) {
      set({ loading: false })
      throw err
    }
  },

  register: async (payload) => {
    set({ loading: true })
    try {
      const { data } = await api.post('/auth/register', payload)
      localStorage.setItem('aqua_token', data.access_token)
      localStorage.setItem('aqua_user', JSON.stringify(data.user))
      set({ token: data.access_token, user: data.user, loading: false })
    } catch (err) {
      set({ loading: false })
      throw err
    }
  },

  setUser: (user, token) => {
    localStorage.setItem('aqua_token', token)
    localStorage.setItem('aqua_user', JSON.stringify(user))
    set({ token, user })
  },

  logout: () => {
    localStorage.removeItem('aqua_token')
    localStorage.removeItem('aqua_user')
    set({ user: null, token: null })
    window.location.href = '/'
  },
}))
